import { ArrowRight, Flame, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteContent } from "@/hooks/useSiteContent";
import { resolveImageUrl } from "@/lib/api";

const FALLBACK_MARQUEE = [
  "The Hillboyz Burger", "The Monster", "Beale Street Magic",
  "The Sunriser Philly", "The Dynamic Duo", "Surf N Turf Burger",
  "Ultimate Nacho Supreme", "Banana Pudding",
];

export const Hero = () => {
  const content = useSiteContent();
  const h = content?.hero || {};
  const marquee = (h.marquee && h.marquee.length ? h.marquee : FALLBACK_MARQUEE);
  const bgUrl = resolveImageUrl(h.image_url || "/assets/photos/IMG_3967.jpeg");

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end pt-24 pb-16"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${bgUrl}')`,
          filter: "saturate(1.45) contrast(1.18) brightness(1.12)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent md:via-black/15 md:to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl hb-fade-up">
          <img
            src="/assets/hillboyz-logo.png"
            alt="HillBoyz Wing and Burger Bar logo"
            className="w-32 sm:w-40 md:w-48 mb-6 drop-shadow-[0_4px_20px_rgba(230,0,0,0.5)]"
            data-testid="hero-logo"
          />
          <div className="inline-flex items-center gap-2 bg-[#E60000] text-white px-3 py-1.5 mb-6 text-xs uppercase tracking-widest font-bold">
            <Flame className="w-4 h-4" strokeWidth={2.5} /> {h.eyebrow || "Hillboyz · Since 2014"}
          </div>
          <h1 className="font-heading text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-white mb-6">
            {h.title_line1 || "More Than"} <br />
            {h.title_line2 || "Just"} <span className="text-[#E60000]">{h.title_accent || "Flavor!"}</span>
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-xl mb-8 leading-relaxed">
            {h.tagline ||
              "Jumbo wings tossed in 31 house sauces. Burgers with an attitude — handmade patties on a deluxe bun. Chicken-shrimp-steak quesadillas that broke the internet. Late night, neighborhood love, served hot."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              data-testid="hero-order-btn"
              to="/menu"
              className="group inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-wider px-7 h-14 transition-colors border-2 border-transparent hover:border-white"
            >
              Order Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
            </Link>
            <Link
              data-testid="hero-visit-btn"
              to="/visit"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-[#333] hover:border-[#E60000] font-bold uppercase tracking-wider px-7 h-14 transition-colors"
            >
              <MapPin className="w-5 h-5" strokeWidth={2.5} /> Find Us
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg">
            <Stat number={h.stat1_number || "31"}   label={h.stat1_label || "Wing Flavors"} />
            <Stat number={h.stat2_number || "100%"} label={h.stat2_label || "Handmade"} />
            <Stat number={h.stat3_number || "901"}  label={h.stat3_label || "Memphis Born"} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-y-2 border-[#E60000] bg-black py-3">
        <div className="hb-marquee font-heading text-2xl md:text-3xl text-white/90">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="inline-flex items-center gap-6 px-6">
              {marquee.map((label, j) => (
                <span key={j} className="inline-flex items-center gap-6">
                  <span>{label}</span>
                  <span className="text-[#E60000]">★</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ number, label }) => (
  <div className="border-l-2 border-[#E60000] pl-3">
    <div className="font-heading text-3xl sm:text-4xl text-white">{number}</div>
    <div className="text-xs uppercase tracking-widest text-white/60">{label}</div>
  </div>
);
